import { useEffect, useRef, useState } from "react";
import styles from "./Admins.module.scss";
import { AdminsService } from "../../services/admin.service";
import { AdminsDataType } from "../../interfaces/admin.interface";
import { RolesService } from "../../services/priviligies.service";
import {
  Empty,
  message,
  Modal,
  Popconfirm,
  PopconfirmProps,
  Select,
} from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { FaPlus } from "react-icons/fa";

const Admins = () => {
  const [admins, setAdmins] = useState<AdminsDataType[]>([]);
  const [roles, setRoles] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [btnDisabledTable, setBtnDisabledTable] = useState(true);
  const [searchIcon, setSearchIcon] = useState(false);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [filteredAdmins, setFilteredAdmins] = useState<AdminsDataType[]>([]);
  const initialAdminValues = useRef<AdminsDataType | null>(null);
  useEffect(() => {
    RolesService.getAllRoles().then((response) => {
      if (Array.isArray(response?.data?.data)) {
        setRoles(response?.data?.data);
      }
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    AdminsService.getAllAdmins()
      .then((response) => {
        if (Array.isArray(response?.data)) {
          const dataWithKeys = response?.data.map(
            (item: AdminsDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setAdmins(dataWithKeys);
          setFilteredAdmins(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
    if (value === "all") {
      setFilteredAdmins(admins);
    } else {
      setFilteredAdmins(admins.filter((admin) => admin.role === value));
    }
  };

  const handleSearchChange = (value: string) => {
    if (value.trim() === "") {
      setFilteredAdmins(admins);
    } else {
      const searchFiltered = admins.filter(
        (admin) =>
          admin.name.toLowerCase().includes(value.toLowerCase()) ||
          admin.email.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredAdmins(searchFiltered);
    }
  };
  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      id: "",
      name: "",
      email: "",
      password: "",
      role: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Adminin adı tələb olunur."),
      email: Yup.string().required("Maili yazmaq tələb olunur."),
      role: Yup.string().required("Rol seçməyiniz tələb olunur."),
      password: !editModalVisible
        ? Yup.string().required("Şifrə tələb olunur.")
        : Yup.string(),
    }),
    onSubmit: async (values) => {
      if (editModalVisible) {
        const isEqual =
          initialAdminValues.current &&
          JSON.stringify(values.name) ===
            JSON.stringify(initialAdminValues.current.name) &&
          JSON.stringify(values.email) ===
            JSON.stringify(initialAdminValues.current.email) &&
          JSON.stringify(values.password) ===
            JSON.stringify(initialAdminValues.current.password) &&
          JSON.stringify(values.role) ===
            JSON.stringify(initialAdminValues.current.role);
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          const response = await AdminsService.updateAdmin(values);
          if (response.status === 200) {
            setLoading(true);
            message.success("Admin updated successfully!");
            setSelectedCheckboxes([]);
            setSelectedRole(null);
            setIsModalVisible(false);
            setEditModalVisible(false);
            resetForm();
            setAdmins((prev: any) =>
              prev.map((admin: any) =>
                admin.id === values.id
                  ? { ...admin, ...values, key: admin.key }
                  : admin
              )
            );
            setFilteredAdmins((prev: any) =>
              prev.map((admin: any) =>
                admin.id === values.id
                  ? { ...admin, ...values, key: admin.key }
                  : admin
              )
            );
          } else {
            message.error(response.errorMessage || "Failed to update admin.");
          }
        }
      } else {
        const response = await AdminsService.addAdmin(values);
        if (response.status === 201) {
          message.success("Admin added successfully!");
          setSelectedCheckboxes([]);
          setSelectedRole(null);
          setLoading(true);
          setIsModalVisible(false);
          setEditModalVisible(false);
          resetForm();
          const updatedData = await AdminsService.getAllAdmins();
          setAdmins(
            updatedData?.data.map((item: AdminsDataType, index: number) => ({
              ...item,
              key: index + 1,
            }))
          );
          setFilteredAdmins(
            updatedData?.data.map((item: AdminsDataType, index: number) => ({
              ...item,
              key: index + 1,
            }))
          );
        } else {
          message.error(response.errorMessage || "Failed to add admin.");
        }
      }
      setLoading(false);
    },
  });

  const handleCheckboxChange = (roleName: string) => {
    setSelectedRole((prev) => (prev === roleName ? null : roleName));
  };
  const handleTableCheckboxChange = (index: number) => {
    setSelectedCheckboxes((prev) => {
      const isSelected = prev.includes(index);
      if (isSelected) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  useEffect(() => {
    setBtnDisabledTable(selectedCheckboxes.length === 0);
  }, [selectedCheckboxes]);

  const roleOptions = [
    { value: "all", label: "Bütün Rollar" },
    ...roles?.map((role) => ({
      value: role,
      label: role,
    })),
  ];

  const cancel: PopconfirmProps["onCancel"] = () => {
    message.error("Nothing deleted");
    setSelectedCheckboxes([]);
  };
  const handleDelete = async () => {
    const idsToDelete = admins
      .filter((admin, index) => selectedCheckboxes.includes(index))
      .map((admin) => admin.id);
    if (idsToDelete.length > 0) {
      const response = await AdminsService.deleteAdmins(idsToDelete);
      if (response.status === 200) {
        message.success("Admins deleted successfully!");
        setAdmins((prevAdmins) =>
          prevAdmins.filter((admin) => !idsToDelete.includes(admin.id))
        );
        setFilteredAdmins((prevAdmins) =>
          prevAdmins.filter((admin) => !idsToDelete.includes(admin.id))
        );
        setSelectedCheckboxes([]);
      } else {
        message.error(response.errorMessage);
      }
    } else {
      message.warning("No admins selected.");
    }
  };

  const handleAdminEdit = (admin: AdminsDataType) => {
    setEditModalVisible(true);
    setFieldValue("id", admin.id);
    setFieldValue("name", admin.name);
    setFieldValue("email", admin.email);
    setFieldValue("password", admin.password);
    setFieldValue("role", admin.role);
    setSelectedRole(admin.role);
    initialAdminValues.current = {
      id: admin.id,
      name: admin.name,
      email: admin.email,
      password: admin.password,
      role: admin.role,
    };
  };
  console.log(filteredAdmins);

  return (
    <div id="pages">
      <div className="page_head">
        <h3 className="page_title">Adminlər</h3>
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <div className="pages_components">
          <div className={styles.table_head}>
            <MainButton
              onClick={() => {
                setIsModalVisible(true);
                resetForm();
                setSelectedRole(null);
              }}
            >
              <FaPlus size={20} />
            </MainButton>
            <div className={styles.table_acts}>
              <div className={styles.search_input}>
                <div
                  className={`${styles.search} ${
                    searchIcon ? styles.search_visible : ""
                  }`}
                >
                  <input
                    type="text"
                    placeholder="Ad və ya e-poçt..."
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                </div>
                {searchIcon ? (
                  <div
                    className={styles.search_icon}
                    onClick={() => {
                      setSearchIcon(false);
                      setFilteredAdmins(admins);
                    }}
                  >
                    <i className="fa-solid fa-x"></i>
                  </div>
                ) : (
                  <div
                    className={styles.search_icon}
                    onClick={() => setSearchIcon(true)}
                  >
                    <i className="fa-solid fa-magnifying-glass"></i>
                  </div>
                )}
              </div>
              <div>
                <Select
                  style={{ width: 140 }}
                  defaultValue="all"
                  options={roleOptions}
                  onChange={handleRoleChange}
                />
              </div>
              <Popconfirm
                title="Bu admini silmək istədiyinizə əminsiniz?"
                onConfirm={handleDelete}
                onCancel={cancel}
                okText="Bəli"
                cancelText="Xeyr"
                placement="topLeft"
              >
                <button className={styles.delete} disabled={btnDisabledTable}>
                  Sil
                </button>
              </Popconfirm>
            </div>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>№</th>
                <th>Adı</th>
                <th>E-poçt ünvanı</th>
                <th>Rolu</th>
                <th>Redaktə edin</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.length > 0 ? (
                filteredAdmins.map((element: any, index: number) => {
                  return (
                    <tr key={index}>
                      <td>
                        {element.role !== "Super Admin" && (
                          <span className={styles.checkbox}>
                            <input
                              type="checkbox"
                              name=""
                              id=""
                              checked={selectedCheckboxes.includes(index)}
                              onChange={() => handleTableCheckboxChange(index)}
                            />
                          </span>
                        )}
                        {element.key}
                      </td>
                      <td>{element.name}</td>
                      <td>{element.email}</td>
                      <td>{element.role}</td>
                      <td>
                        <div
                          className="edit"
                          onClick={() => handleAdminEdit(element)}
                        >
                          <i className="fa-solid fa-pen-to-square"></i>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr className={styles.no_data}>
                  <div className={styles.no_data_icon}>
                    <Empty description="No data" />
                  </div>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        title={
          editModalVisible
            ? "Adminin məlumatlarını redaktə edin"
            : "Admin əlavə edin"
        }
        open={isModalVisible || editModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditModalVisible(false);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="name">
              Adminin adı
            </label>
            <div className="form_input">
              <div className="input_wrapper">
                <input
                  name="name"
                  id="name"
                  placeholder="Adminin adı"
                  value={values.name}
                  onChange={handleChange}
                />
                <span className="focus_border"></span>
              </div>
              <ErrorMessage error={errors.name} touched={touched.name} />
            </div>
          </div>
          <div className="form_input">
            <label className="label" htmlFor="email">
              Adminin e-poçt ünvanı
            </label>
            <div className="input_wrapper">
              <input
                type="email"
                name="email"
                id="email"
                placeholder="Adminin e-poçt ünvanı"
                value={values.email}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
              <ErrorMessage error={errors.email} touched={touched.email} />
            </div>
          </div>
          <div className="form_input">
            <label className="label" htmlFor="password">
              Adminin parolu
            </label>
            <div className="input_wrapper">
              <input
                autoComplete="new-password"
                type="password"
                name="password"
                id="password"
                placeholder="Adminin parolu"
                value={values.password}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
              <ErrorMessage
                error={errors.password}
                touched={touched.password}
              />
            </div>
          </div>
          <div>
            {values.role === "Super Admin" ? null : (
              <div className="form_input">
                <h4 className="roles_title">Rollar</h4>
                <div className="checkbox_wrapper">
                  {roles
                    .filter((role: string) => role !== "Super Admin")
                    .map((role: string, idx: number) => (
                      <div key={idx} className="checkbox">
                        <input
                          type="checkbox"
                          value={role}
                          id={role}
                          name="role"
                          disabled={
                            selectedRole !== null && selectedRole !== role
                          }
                          checked={values.role === role}
                          onChange={() => {
                            handleCheckboxChange(role);
                            setFieldValue(
                              "role",
                              selectedRole === role ? "" : role
                            );
                          }}
                        />
                        <label htmlFor={role}>{role}</label>
                      </div>
                    ))}
                </div>
                <ErrorMessage error={errors.role} touched={touched.role} />
              </div>
            )}
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {editModalVisible ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Admins;
